"use client"

import * as React from "react"
import {
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
} from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from "@workspace/ui/components/alert-dialog"
import { cn } from "@workspace/ui/lib/utils"

/**
 * Blocking, single-outcome notice — the "sweet alert" shape: tinted icon
 * medallion, headline, explanation, one button that dismisses it.
 *
 * Use this ONLY where a toast is too easy to miss and the diner genuinely has
 * to stop and read: a declined payment, an order that didn't go through, a
 * checkout that can't continue. Everything else (confirmations, retryable
 * field errors, "code sent") stays a toast — a modal for routine feedback just
 * trains people to dismiss modals without reading them.
 *
 * Purely presentational and fully controlled: the caller owns `open` and
 * decides what dismissal means (`@/components/alert-flash` derives it from the
 * URL; a form can drive it from its own submit state).
 */

export type AlertNoticeTone = "error" | "warning" | "success" | "info"

const TONE_ICON: Record<
  AlertNoticeTone,
  React.ComponentType<{ className?: string }>
> = {
  error: CircleAlertIcon,
  warning: TriangleAlertIcon,
  success: CircleCheckIcon,
  info: InfoIcon,
}

// Explicit dark variants, same reasoning as the confirmation screen's status
// pills: a tenant theme can be light or dark, and `Badge`/`bg-muted` carry no
// success/warning colour of their own.
const TONE_MEDIA: Record<AlertNoticeTone, string> = {
  error: "bg-destructive/10 text-destructive",
  warning:
    "bg-amber-500/15 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
  success:
    "bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  info: "bg-sky-500/15 text-sky-700 dark:bg-sky-500/20 dark:text-sky-400",
}

export function AlertNotice({
  open,
  onOpenChange,
  tone = "error",
  title,
  description,
  actionLabel,
  onAction,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  tone?: AlertNoticeTone
  title: string
  description: string
  /** the single dismiss button's label */
  actionLabel: string
  /** extra work on dismiss; the dialog closes either way */
  onAction?: () => void
}) {
  const Icon = TONE_ICON[tone]

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent size="sm">
        <AlertDialogHeader>
          <AlertDialogMedia className={cn("rounded-full", TONE_MEDIA[tone])}>
            <Icon aria-hidden />
          </AlertDialogMedia>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="grid-cols-1!">
          <AlertDialogAction onClick={onAction}>
            {actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
